import _extends from "@babel/runtime/helpers/esm/extends";
import { EMPTY_OBJECT } from '@base-ui/utils/empty';
import { DEFAULT_SCHEDULER_PREFERENCES, SchedulerStore } from '@mui/x-scheduler-headless/internals';
import { createChangeEventDetails } from '@mui/x-scheduler-headless/base-ui-copy';
import { EventTimelinePremiumLazyLoadingPlugin } from "./plugins/EventTimelinePremiumLazyLoadingPlugin.mjs";
import { EVENT_TIMELINE_PREMIUM_VIEW_CONFIGS } from "../event-timeline-premium-selectors/eventTimelinePremiumViewSelectors.mjs";

// TODO(#21359): In the future, this config should become a prop so users can customize step sizes per view.
const VIEW_NAVIGATION_STEP = {
  time: (adapter, date, amount) => adapter.addDays(date, amount),
  days: (adapter, date, amount) => adapter.addDays(date, amount),
  weeks: (adapter, date, amount) => adapter.addWeeks(date, amount),
  months: (adapter, date, amount) => adapter.addMonths(date, amount),
  years: (adapter, date, amount) => adapter.addYears(date, amount)
};
export const DEFAULT_VIEWS = ['time', 'days', 'weeks', 'months', 'years'];
export const DEFAULT_VIEW = 'time';
const deriveStateFromParameters = parameters => ({
  views: parameters.views ?? DEFAULT_VIEWS
});
export const DEFAULT_PREFERENCES = DEFAULT_SCHEDULER_PREFERENCES;
const mapper = {
  getInitialState: (schedulerInitialState, parameters) => _extends({}, schedulerInitialState, deriveStateFromParameters(parameters), {
    view: parameters.view ?? parameters.defaultView ?? DEFAULT_VIEW,
    preferences: parameters.preferences ?? parameters.defaultPreferences ?? EMPTY_OBJECT
  }),
  updateStateFromParameters: (newSchedulerState, parameters, updateModel) => {
    const newState = _extends({}, newSchedulerState, deriveStateFromParameters(parameters));
    updateModel(newState, 'view', 'defaultView');
    updateModel(newState, 'preferences', 'defaultPreferences');
    return newState;
  }
};
export class EventTimelinePremiumStore extends SchedulerStore {
  constructor(parameters, adapter) {
    super(parameters, adapter, 'EventTimelinePremiumStore', mapper);
    if (process.env.NODE_ENV !== 'production') {
      // Add listeners to assert the state validity (not applied in prod)
      this.subscribe(state => {
        this.assertViewValidity(state.view);
        return null;
      });
    }
    this.lazyLoading = new EventTimelinePremiumLazyLoadingPlugin(this);
  }
  assertViewValidity(view) {
    const views = this.state.views;
    if (!views.includes(view)) {
      throw /* FIXME (minify-errors-in-prod): Unminifyable error in production! */new Error([`MUI: The component tried to switch to the "${view}" view but it is not compatible with the available views: ${views.join(', ')}.`, 'Please ensure that the requested view is included in the views array.'].join('\n'));
    }
  }
  buildPublicAPI() {
    return _extends({}, super.buildPublicAPI(), {
      goToNextVisibleDate: this.goToNextVisibleDate,
      goToPreviousVisibleDate: this.goToPreviousVisibleDate
    });
  }

  /**
   * Goes to the next visible date span based on the current view.
   */
  goToNextVisibleDate = event => {
    const {
      adapter,
      visibleDate,
      view
    } = this.state;
    const {
      unitCount
    } = EVENT_TIMELINE_PREMIUM_VIEW_CONFIGS[view];
    const navigate = VIEW_NAVIGATION_STEP[view];
    this.setVisibleDate({
      visibleDate: navigate(adapter, visibleDate, unitCount),
      event
    });
  };

  /**
   * Goes to the previous visible date span based on the current view.
   */
  goToPreviousVisibleDate = event => {
    const {
      adapter,
      visibleDate,
      view
    } = this.state;
    const {
      unitCount
    } = EVENT_TIMELINE_PREMIUM_VIEW_CONFIGS[view];
    const navigate = VIEW_NAVIGATION_STEP[view];
    this.setVisibleDate({
      visibleDate: navigate(adapter, visibleDate, -unitCount),
      event
    });
  };

  /**
   * Sets the view of the timeline.
   */
  setView = (view, event) => {
    const {
      view: viewProp,
      onViewChange
    } = this.parameters;
    if (view !== this.state.view) {
      this.assertViewValidity(view);
      const eventDetails = createChangeEventDetails('none', event);
      onViewChange?.(view, eventDetails);
      if (!eventDetails.isCanceled && viewProp === undefined) {
        this.set('view', view);
      }
    }
  };
}